import React, {PropsWithChildren} from 'react';
import classNames from 'classnames';
import {
    CustomResizableLayout,
    ResizablePanel
} from '../../../../components/layout/resizable-layout';
import styles from '../query-layout.module.css';
import {isEditableQueryDraft} from '../../utils';
import {LocalErrorBoundary} from '../../../../components/error-boundary';
import {ResultChainsPanel} from '../../../../components/graph/result-chains-panel/path-result-panel/result-chains-panel';
import {
    collapsedFactsetPanelHeight,
    GraphResultChainsPanel
} from './result-chains-panel/graph-result-chains-panel';
import {useResultsListProps} from './result-chains-panel/use-results-list-props';
import {useQueryLayoutProps} from '../../hooks/use-query-layout-props';
import {RightPanelNavBar} from './right-panel/right-panel-nav-bar';

interface Props {
    selectedQuery: CompletedQuery;
    resultsListProps: ReturnType<typeof useResultsListProps>;
    queryLayoutProps: ReturnType<typeof useQueryLayoutProps>;
    clearNodeSelection: () => void;
    clearResultListItemSelection: () => void;
    selectedNodes: number[];
    maxPathLength: number;
    canFetchNodeStructure: boolean;
}

export function CompletedQueryLayout({
    selectedQuery,
    resultsListProps,
    queryLayoutProps,
    selectedNodes,
    maxPathLength,
    clearNodeSelection,
    clearResultListItemSelection,
    children,
    canFetchNodeStructure
}: PropsWithChildren<Props>) {
    const hasResultsPanel =
        Boolean(resultsListProps.selectedResultItem) || selectedNodes.length > 0;

    const factsetPanelFixedSize = (function () {
        if (!hasResultsPanel) {
            return {panelIndex: 1 as const, size: 0};
        }

        if (queryLayoutProps.isFactsetCollapsed) {
            return {panelIndex: 1 as const, size: collapsedFactsetPanelHeight};
        }

        return undefined;
    })();

    return (
        <React.Fragment>
            <CustomResizableLayout
                localStorageKey="queries.results.chain.layoutSize"
                settings={{initialFirstPanelShare: 0.6, minPermittedHeight: 200}}
                fixedPanelSize={factsetPanelFixedSize}
                alignment="vertical"
            >
                {(resizableLayoutProps) => (
                    <React.Fragment>
                        <ResizablePanel
                            index={0}
                            {...resizableLayoutProps}
                            className={classNames(styles.queryLayout, {
                                [styles.viewMode]: !isEditableQueryDraft(selectedQuery)
                            })}
                        >
                            {children}
                        </ResizablePanel>

                        {!!resultsListProps.selectedResultItem && (
                            <LocalErrorBoundary variant="empty">
                                <ResizablePanel
                                    className={styles.factsetPanelBottom}
                                    index={1}
                                    {...resizableLayoutProps}
                                >
                                    <ResultChainsPanel
                                        queryInfo={selectedQuery}
                                        connectionId={resultsListProps.selectedResultItem}
                                        closePanel={clearResultListItemSelection}
                                        selectedConnectionIndex={
                                            resultsListProps.selectedConnectionIndex!
                                        }
                                        isPanelCollapsed={queryLayoutProps.isFactsetCollapsed}
                                        toggleIsPanelCollapsed={
                                            queryLayoutProps.toggleIsFactsetCollapsed
                                        }
                                    />
                                </ResizablePanel>
                            </LocalErrorBoundary>
                        )}

                        {selectedNodes.length > 0 && (
                            <LocalErrorBoundary variant="empty">
                                <ResizablePanel
                                    className={styles.factsetPanelBottom}
                                    index={1}
                                    {...resizableLayoutProps}
                                >
                                    <GraphResultChainsPanel
                                        queryInfo={selectedQuery}
                                        selectedNodes={selectedNodes}
                                        clearNodeSelection={clearNodeSelection}
                                        isPanelCollapsed={queryLayoutProps.isFactsetCollapsed}
                                        toggleIsPanelCollapsed={
                                            queryLayoutProps.toggleIsFactsetCollapsed
                                        }
                                        maxPathLength={maxPathLength}
                                        canFetchNodeStructure={canFetchNodeStructure}
                                    />
                                </ResizablePanel>
                            </LocalErrorBoundary>
                        )}
                    </React.Fragment>
                )}
            </CustomResizableLayout>

            <RightPanelNavBar queryLayoutProps={queryLayoutProps} />
        </React.Fragment>
    );
}
